import { Box, type BoxProps } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';

interface EmailHtmlPreviewProps extends Omit<BoxProps, 'children'> {
  html: string;
  fallbackText?: string;
}

/**
 * Wraps Mustache-style {{variable}} tokens in styled spans
 * to match the editor's variable pill appearance.
 */
function styleVariables(html: string): string {
  return html.replace(/\{\{(\w+)\}\}/g, '<span class="email-variable">{{$1}}</span>');
}

const EmailHtmlPreview: React.FC<EmailHtmlPreviewProps> = ({ html, fallbackText, ...boxProps }) => {
  const processedHtml = useMemo(() => {
    const sanitized = DOMPurify.sanitize(html || '', {
      FORBID_TAGS: ['style'],
      FORBID_ATTR: ['style'],
    });
    return sanitized ? styleVariables(sanitized) : '';
  }, [html]);

  const content =
    processedHtml ||
    (fallbackText ? `<p style="color:var(--chakra-colors-fg-subtle)">${fallbackText}</p>` : '');

  if (!content) return null;

  return (
    <Box
      overflow="auto"
      fontSize="14px"
      lineHeight="1.7"
      css={{
        '& p': { marginBottom: '0.75em' },
        '& ul, & ol': { paddingLeft: '1.5em', marginBottom: '0.75em' },
        '& h2': { fontSize: '1.25em', fontWeight: 600, marginBottom: '0.5em' },
        '& a': { color: 'var(--chakra-colors-primary-600)', textDecoration: 'underline' },
        '& .email-variable': {
          backgroundColor: 'var(--chakra-colors-primary-50)',
          color: 'var(--chakra-colors-primary-700)',
          padding: '0 4px',
          borderRadius: '4px',
          fontWeight: 500,
          fontSize: 'inherit',
        },
      }}
      dangerouslySetInnerHTML={{ __html: content }}
      {...boxProps}
    />
  );
};

export default EmailHtmlPreview;
